import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, filter, switchMap, take } from 'rxjs';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import IncidentService from 'src/app/shared/services/incident.service';
import { CreateIncidentRequest, EditIncidentRequest, Incident, IncidentCategory } from '../../incident-models';
import {
  ChargingStationLocation,
  ChargingStationLocationConnector,
  ChargingStationLocationSearchEvent,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { PageEvent } from '@angular/material/paginator';
import { EnumToArrayPipe } from '../../../../shared/pipes/enum-to-array.pipe';
import { FormatIncidentPipe } from '../../../../shared/pipes/format-incident.pipe';
import { TranslateIncidentCategoryEnum } from '../../../../shared/pipes/translate-incident-category-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'adm-incident-create-or-edit-modal',
  templateUrl: './incident-create-or-edit-modal.component.html',
  styleUrls: ['./incident-create-or-edit-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatTooltipModule,
    EnumToArrayPipe,
    FormatIncidentPipe,
    TranslateIncidentCategoryEnum,
    CustomDropDownArrowDirective,
  ],
})
export class IncidentCreateOrEditModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<IncidentCreateOrEditModalComponent>>(MatDialogRef);
  private chargingStationLocationService = inject(ChargingStationLocationService);
  private incidentService = inject(IncidentService);
  data = inject<{
    isEdit: boolean;
    incident: Incident;
  }>(MAT_DIALOG_DATA);

  isEdit: boolean;
  incident: Incident;

  connectors = new Array<ChargingStationLocationConnector>();
  form: FormGroup;

  get location() {
    return this.form.get('location') as FormControl<ChargingStationLocation | string | null>;
  }

  get connectorIdentifier() {
    return this.form.get('connectorIdentifier') as FormControl<string | null>;
  }

  get category() {
    return this.form.get('category') as FormControl<IncidentCategory | null>;
  }

  get description() {
    return this.form.get('description') as FormControl<string | null>;
  }

  filteredOptions: Array<ChargingStationLocation>;

  categories = IncidentCategory;

  ngOnInit(): void {
    this.isEdit = this.data.isEdit;

    this.form = this.fb.group({
      location: [{ value: null, disabled: this.isEdit }, Validators.required],
      connectorIdentifier: [{ value: null, disabled: true }, Validators.required],
      category: [null, Validators.required],
      description: ['', Validators.required],
    });

    if (this.isEdit) {
      this.initializeEdit();
    }
    this.location.valueChanges
      .pipe(
        filter(val => typeof val === 'string' && val.length >= 2),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          const pageEvent = new PageEvent();
          pageEvent.pageSize = 25;
          const searchEvent = new ChargingStationLocationSearchEvent(pageEvent);
          searchEvent.query = value as string;
          return this.chargingStationLocationService.getChargingStationLocations(searchEvent);
        })
      )
      .subscribe(x => {
        this.filteredOptions = x.items;
      });

    this.location.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(location => {
      if (location && typeof location !== 'string' && location.identifier) {
        this.chargingStationLocationService.getConnectorsOnLocation(location.identifier).subscribe(resp => {
          this.connectors = resp.connectors;
          this.connectorIdentifier.setValue(null);
          this.connectorIdentifier.enable();
        });
      }
    });
  }

  initializeEdit() {
    this.form.markAsUntouched();

    this.incident = this.data.incident;
    this.description.setValue(this.incident.description);
    this.connectorIdentifier.setValue(this.incident.connectorIdentifier);
    this.location.setValue(this.displayLocation(this.incident.incidentLocation));

    this.chargingStationLocationService
      .getConnectorsOnLocation(this.incident.incidentLocation.identifier)
      .pipe(take(1))
      .subscribe(resp => {
        this.connectors = resp.connectors;
        this.connectorIdentifier.setValue(this.incident.connectorIdentifier);
      });

    //const enumValue: IncidentCategory = IncidentCategory[this.incident.category.toString() as keyof typeof IncidentCategory];
    this.category.setValue(this.incident.category);
  }

  displayLocation(location: ChargingStationLocation | Incident['incidentLocation'] | string | null): string {
    if (!location) {
      return '';
    }
    if (typeof location === 'string') {
      return location;
    }
    return `${location.name}, ${location.road} ${location.number}${location.letter}, ${location.postalCode} ${location.city}`;
  }

  submitForm() {
    if (this.form.valid) {
      if (!this.isEdit) {
        if (!this.category.value || !this.description.value || !this.connectorIdentifier.value) {
          this.form.markAllAsTouched();
          return;
        }

        const request: CreateIncidentRequest = {
          category: this.category.value,
          description: this.description.value,
          connectorIdentifier: this.connectorIdentifier.value,
        };
        this.incidentService.createIncident(request).subscribe(() => {
          this.close(true);
        });
      } else {
        if (!this.category.value || !this.description.value) {
          this.form.markAllAsTouched();
          return;
        }

        const request: EditIncidentRequest = {
          category: this.category.value,
          description: this.description.value,
          incidentNumber: this.incident.number,
        };

        this.incidentService.editIncident(request).subscribe(() => {
          this.close(true);
        });
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  close(refresh: boolean) {
    this.dialogRef.close(refresh);
  }
}
